import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Clock, Calendar, Plus, Trash2, Users, Eye, Edit, Power, AlertTriangle, CheckSquare, Loader2, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EscalasCiclicasPage() {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const tabInicial = searchParams.get('tab') || 'escalas'; // Default para escalas
  const [activeTab, setActiveTab] = useState(tabInicial);
  const [turnos, setTurnos] = useState([]);
  const [escalas, setEscalas] = useState([]);
  const [empregados, setEmpregados] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [showTurnoDialog, setShowTurnoDialog] = useState(false);
  const [showEditTurnoDialog, setShowEditTurnoDialog] = useState(false);
  const [showEscalaDialog, setShowEscalaDialog] = useState(false);
  const [showEditEscalaDialog, setShowEditEscalaDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showVinculoDialog, setShowVinculoDialog] = useState(false);
  const [showVinculoLoteDialog, setShowVinculoLoteDialog] = useState(false);
  const [showSimuladorDialog, setShowSimuladorDialog] = useState(false);
  
  // Selected items
  const [selectedEscala, setSelectedEscala] = useState(null);
  const [selectedTurno, setSelectedTurno] = useState(null);
  const [selectedEmpregado, setSelectedEmpregado] = useState(null);
  const [simulacao, setSimulacao] = useState(null);
  const [empregadosVinculados, setEmpregadosVinculados] = useState([]);
  
  // Vínculo em lote
  const [empregadosSelecionados, setEmpregadosSelecionados] = useState([]);
  const [vinculoLoteLoading, setVinculoLoteLoading] = useState(false);
  const [vinculoLote, setVinculoLote] = useState({
    escala_id: '',
    data_inicio: new Date().toISOString().split('T')[0],
    data_primeira_folga: ''
  });
  const [filtroVinculo, setFiltroVinculo] = useState('todos'); // todos, sem_escala, com_escala

  const empresaId = user?.empresa_id;

  const [novoTurno, setNovoTurno] = useState({
    nome: '',
    tipo: 'manha',
    hora_inicio: '08:00',
    hora_fim: '17:00',
    intervalo_inicio: '12:00',
    intervalo_fim: '13:00',
    horas_trabalhadas: 8
  });

  const [novaEscala, setNovaEscala] = useState({
    nome: '',
    tipo: 'ciclica',
    tamanho_ciclo: 2,
    tolerancia_minutos: 10,
    horas_semanais: 44,
    dias: []
  });

  const [novoVinculo, setNovoVinculo] = useState({
    empregado_id: '',
    escala_id: '',
    data_inicio: new Date().toISOString().split('T')[0],
    data_primeira_folga: '',
    observacao: ''
  });

  useEffect(() => {
    if (token && empresaId) {
      fetchData();
    }
  }, [token, empresaId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [turnosRes, escalasRes, empregadosRes] = await Promise.all([
        fetch(`${API_URL}/api/turnos?empresa_id=${empresaId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/escalas-trabalho?empresa_id=${empresaId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/empregados?empresa_id=${empresaId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (turnosRes.ok) setTurnos(await turnosRes.json());
      if (escalasRes.ok) setEscalas(await escalasRes.json());
      if (empregadosRes.ok) setEmpregados(await empregadosRes.json());
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // ==================== TURNO HANDLERS ====================
  const handleCreateTurno = async () => {
    try {
      const response = await fetch(`${API_URL}/api/turnos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ...novoTurno, empresa_id: empresaId })
      });

      if (response.ok) {
        toast.success('Turno criado com sucesso');
        setShowTurnoDialog(false);
        setNovoTurno({
          nome: '',
          tipo: 'manha',
          hora_inicio: '08:00',
          hora_fim: '17:00',
          intervalo_inicio: '12:00',
          intervalo_fim: '13:00',
          horas_trabalhadas: 8
        });
        fetchData();
      } else {
        toast.error('Erro ao criar turno');
      }
    } catch (error) {
      toast.error('Erro ao criar turno');
    }
  };

  const handleDeleteTurno = async (turnoId) => {
    if (!window.confirm('Deseja desativar este turno?')) return;

    try {
      const response = await fetch(`${API_URL}/api/turnos/${turnoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Turno desativado');
        fetchData();
      }
    } catch (error) {
      toast.error('Erro ao desativar turno');
    }
  };

  const openEditTurnoDialog = (turno) => {
    setSelectedTurno({ ...turno });
    setShowEditTurnoDialog(true);
  };

  const handleEditTurno = async () => {
    if (!selectedTurno) return;
    
    if (!selectedTurno.nome.trim()) {
      toast.error('Digite um nome para o turno');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/turnos/${selectedTurno.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          nome: selectedTurno.nome,
          tipo: selectedTurno.tipo,
          hora_inicio: selectedTurno.hora_inicio,
          hora_fim: selectedTurno.hora_fim,
          intervalo_inicio: selectedTurno.intervalo_inicio || null,
          intervalo_fim: selectedTurno.intervalo_fim || null,
          horas_trabalhadas: selectedTurno.horas_trabalhadas
        })
      });

      if (response.ok) {
        toast.success('Turno atualizado com sucesso');
        setShowEditTurnoDialog(false);
        setSelectedTurno(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao atualizar turno');
      }
    } catch (error) {
      toast.error('Erro ao atualizar turno');
    }
  };

  const handleInativarTurno = async (turnoId) => {
    try {
      const response = await fetch(`${API_URL}/api/turnos/${turnoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Turno inativado com sucesso');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao inativar turno');
      }
    } catch (error) {
      toast.error('Erro ao inativar turno');
    }
  };

  // ==================== ESCALA HANDLERS ====================
  
  // Gera padrão automático: metade trabalha, metade folga
  const gerarPadraoAutomatico = (tamanho) => {
    const dias = [];
    const diasTrabalho = Math.ceil(tamanho / 2);
    
    for (let i = 1; i <= tamanho; i++) {
      dias.push({
        dia_ciclo: i,
        turno_id: null,
        trabalha: i <= diasTrabalho,
        compensado: false,
        hora_inicio: null,
        hora_fim: null,
        intervalo_inicio: null,
        intervalo_fim: null,
        descricao: i <= diasTrabalho ? 'Trabalho' : 'Folga'
      });
    }
    return dias;
  };

  const updateDiasCiclo = (tamanho) => {
    if (tamanho < 1 || tamanho > 60) return;
    const dias = gerarPadraoAutomatico(tamanho);
    setNovaEscala({ ...novaEscala, tamanho_ciclo: tamanho, dias: dias });
  };

  const handleCreateEscala = async () => {
    if (!novaEscala.nome.trim()) {
      toast.error('Digite um nome para a escala');
      return;
    }
    
    if (novaEscala.dias.length !== novaEscala.tamanho_ciclo) {
      toast.error(`Configure exatamente ${novaEscala.tamanho_ciclo} dias no ciclo`);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/escalas-trabalho`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ...novaEscala, empresa_id: empresaId })
      });

      if (response.ok) {
        toast.success('Escala criada com sucesso');
        setShowEscalaDialog(false);
        setNovaEscala({
          nome: '',
          tipo: 'ciclica',
          tamanho_ciclo: 2,
          tolerancia_minutos: 10,
          horas_semanais: 44,
          dias: []
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao criar escala');
      }
    } catch (error) {
      toast.error('Erro ao criar escala');
    }
  };

  const handleEditEscala = async () => {
    if (!selectedEscala) return;
    
    if (!selectedEscala.nome.trim()) {
      toast.error('Digite um nome para a escala');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/escalas-trabalho/${selectedEscala.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          nome: selectedEscala.nome,
          tipo: selectedEscala.tipo || 'ciclica',
          tamanho_ciclo: selectedEscala.tamanho_ciclo || selectedEscala.dias?.length || 2,
          tolerancia_minutos: selectedEscala.tolerancia_minutos,
          horas_semanais: selectedEscala.horas_semanais || 44,
          dias: selectedEscala.dias
        })
      });

      if (response.ok) {
        toast.success('Escala atualizada com sucesso');
        setShowEditEscalaDialog(false);
        setSelectedEscala(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao atualizar escala');
      }
    } catch (error) {
      toast.error('Erro ao atualizar escala');
    }
  };

  const openEditDialog = (escala) => {
    setSelectedEscala({ ...escala });
    setShowEditEscalaDialog(true);
  };

  const checkEmpregadosVinculados = async (escalaId) => {
    const vinculados = empregados.filter(emp => emp.escala_id === escalaId);
    setEmpregadosVinculados(vinculados);
    return vinculados;
  };

  const openDeleteDialog = async (escala) => {
    setSelectedEscala(escala);
    await checkEmpregadosVinculados(escala.id);
    setShowDeleteDialog(true);
  };

  const handleDeleteEscala = async () => {
    if (!selectedEscala) return;
    
    // Verificar se há empregados vinculados
    const vinculados = empregados.filter(emp => emp.escala_id === selectedEscala.id);
    if (vinculados.length > 0) {
      toast.error(`Não é possível excluir. ${vinculados.length} empregado(s) vinculado(s).`);
      return;
    }

    try {
      // Usando DELETE real para exclusão permanente
      const response = await fetch(`${API_URL}/api/escalas-trabalho/${selectedEscala.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Escala excluída com sucesso');
        setShowDeleteDialog(false);
        setSelectedEscala(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao excluir escala');
      }
    } catch (error) {
      toast.error('Erro ao excluir escala');
    }
  };

  const handleInativarEscala = async () => {
    if (!selectedEscala) return;

    try {
      const response = await fetch(`${API_URL}/api/escalas-trabalho/${selectedEscala.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Escala inativada com sucesso');
        setShowDeleteDialog(false);
        setSelectedEscala(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao inativar escala');
      }
    } catch (error) {
      toast.error('Erro ao inativar escala');
    }
  };

  // ==================== VÍNCULO HANDLERS ====================
  const handleVincularEmpregado = async () => {
    if (!novoVinculo.empregado_id || !novoVinculo.escala_id) {
      toast.error('Selecione o empregado e a escala');
      return;
    }
    if (!novoVinculo.data_primeira_folga) {
      toast.error('Informe a data da primeira folga');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/escalas-trabalho/${novoVinculo.escala_id}/vincular-empregado`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            empregado_id: novoVinculo.empregado_id,
            data_inicio: novoVinculo.data_inicio,
            data_primeira_folga: novoVinculo.data_primeira_folga,
            observacao: novoVinculo.observacao || null
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(`Empregado vinculado à escala. Dia ${result.dia_inicio_ciclo} do ciclo.`);
        setShowVinculoDialog(false);
        setNovoVinculo({
          empregado_id: '',
          escala_id: '',
          data_inicio: new Date().toISOString().split('T')[0],
          data_primeira_folga: '',
          observacao: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao vincular empregado');
      }
    } catch (error) {
      toast.error('Erro ao vincular empregado');
    }
  };

  // ==================== VÍNCULO EM LOTE ====================
  const handleVincularLote = async () => {
    if (empregadosSelecionados.length === 0) {
      toast.error('Selecione pelo menos um empregado');
      return;
    }
    if (!vinculoLote.escala_id) {
      toast.error('Selecione uma escala');
      return;
    }
    if (!vinculoLote.data_primeira_folga) {
      toast.error('Informe a data da primeira folga');
      return;
    }

    setVinculoLoteLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/empregados/vincular-escala-lote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          empregado_ids: empregadosSelecionados,
          escala_id: vinculoLote.escala_id,
          data_inicio: vinculoLote.data_inicio,
          data_primeira_folga: vinculoLote.data_primeira_folga
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(
          <div>
            <strong>{data.vinculados} empregado(s)</strong> vinculado(s) à escala <strong>{data.escala_nome}</strong>
            {data.erros > 0 && <div className="text-amber-200 text-xs mt-1">{data.erros} erro(s)</div>}
          </div>
        );
        setShowVinculoLoteDialog(false);
        setEmpregadosSelecionados([]);
        setVinculoLote({
          escala_id: '',
          data_inicio: new Date().toISOString().split('T')[0],
          data_primeira_folga: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao vincular empregados em lote');
      }
    } catch (error) {
      toast.error('Erro ao vincular empregados em lote');
    } finally {
      setVinculoLoteLoading(false);
    }
  };

  const toggleEmpregadoSelecionado = (empId) => {
    setEmpregadosSelecionados(prev => 
      prev.includes(empId) 
        ? prev.filter(id => id !== empId) 
        : [...prev, empId]
    );
  };

  const toggleTodosEmpregados = () => {
    const empregadosFiltrados = getEmpregadosFiltradosParaLote();
    if (empregadosSelecionados.length === empregadosFiltrados.length) {
      setEmpregadosSelecionados([]);
    } else {
      setEmpregadosSelecionados(empregadosFiltrados.map(e => e.id));
    }
  };

  const getEmpregadosFiltradosParaLote = () => {
    return empregados.filter(emp => {
      // Apenas empregados ativos que controlam ponto
      if (!emp.ativo || emp.nao_controla_ponto) return false;
      if (filtroVinculo === 'sem_escala') return !emp.escala_id;
      if (filtroVinculo === 'com_escala') return !!emp.escala_id;
      return true;
    });
  };

  const handleSimular = async (escalaId, empregadoId, tipoEscala = null) => {
    try {
      const hoje = new Date().toISOString().split('T')[0];
      
      // Buscar apenas na coleção escalas_trabalho
      const response = await fetch(
        `${API_URL}/api/escalas-trabalho/${escalaId}/simular?empregado_id=${empregadoId}&data_inicio=${hoje}&dias=30`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setSimulacao(data);
        setShowSimuladorDialog(true);
      } else {
        toast.error('Erro ao simular escala - escala não encontrada');
      }
    } catch (error) {
      console.error('Erro ao simular:', error);
      toast.error('Erro ao simular escala');
    }
  };

  // ==================== RENDER HELPERS ====================
  const tiposTurno = [
    { value: 'manha', label: 'Manhã' },
    { value: 'tarde', label: 'Tarde' },
    { value: 'noite', label: 'Noite' },
    { value: 'integral', label: 'Integral' },
    { value: 'folga', label: 'Folga' }
  ];

  const tiposDia = [
    { value: 'trabalha', label: 'Trabalha' },
    { value: 'folga', label: 'Folga' },
    { value: 'compensado', label: 'Compensado' }
  ];

  const getTipoDia = (dia) => {
    if (dia.compensado) return 'compensado';
    if (dia.trabalha) return 'trabalha';
    return 'folga';
  };

  const handleTipoDiaChange = (value, idx, escalaState, setEscalaState) => {
    const dias = [...escalaState.dias];
    dias[idx].trabalha = value === 'trabalha' || value === 'compensado';
    dias[idx].compensado = value === 'compensado';
    if (value === 'folga') {
      dias[idx].turno_id = null;
      dias[idx].hora_inicio = null;
      dias[idx].hora_fim = null;
      dias[idx].intervalo_inicio = null;
      dias[idx].intervalo_fim = null;
    }
    setEscalaState({ ...escalaState, dias: dias });
  };

  const handleHorarioChange = (field, value, idx, escalaState, setEscalaState) => {
    const dias = [...escalaState.dias];
    dias[idx][field] = value || null;
    setEscalaState({ ...escalaState, dias: dias });
  };

  const renderDiaConfig = (dia, idx, escalaState, setEscalaState) => {
    const tipoDia = getTipoDia(dia);
    const showHorarios = tipoDia === 'trabalha' || tipoDia === 'compensado';
    
    return (
      <div key={idx} className="p-3 bg-slate-50 rounded-lg space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-200 font-bold text-slate-700">
            {dia.dia_ciclo}
          </div>
          <Select
            value={tipoDia}
            onValueChange={(value) => handleTipoDiaChange(value, idx, escalaState, setEscalaState)}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tiposDia.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {showHorarios && (
            <Select
              value={dia.turno_id || 'custom'}
              onValueChange={(value) => {
                const dias = [...escalaState.dias];
                if (value === 'custom') {
                  dias[idx].turno_id = null;
                } else {
                  dias[idx].turno_id = value;
                  // Preencher horários do turno selecionado
                  const turno = turnos.find(t => t.id === value);
                  if (turno) {
                    dias[idx].hora_inicio = turno.hora_inicio;
                    dias[idx].hora_fim = turno.hora_fim;
                    dias[idx].intervalo_inicio = turno.intervalo_inicio;
                    dias[idx].intervalo_fim = turno.intervalo_fim;
                  }
                }
                setEscalaState({ ...escalaState, dias: dias });
              }}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Turno ou horário customizado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Horário Customizado</SelectItem>
                {turnos.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.nome} ({t.hora_inicio} - {t.hora_fim})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {!showHorarios && (
            <span className="flex-1 text-slate-400 italic">Dia de folga</span>
          )}
        </div>
        
        {/* Campos de horário editáveis */}
        {showHorarios && (
          <div className="ml-13 grid grid-cols-4 gap-2">
            <div>
              <Label className="text-xs text-slate-500">Entrada</Label>
              <Input
                type="time"
                value={dia.hora_inicio || ''}
                onChange={(e) => handleHorarioChange('hora_inicio', e.target.value, idx, escalaState, setEscalaState)}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Saída</Label>
              <Input
                type="time"
                value={dia.hora_fim || ''}
                onChange={(e) => handleHorarioChange('hora_fim', e.target.value, idx, escalaState, setEscalaState)}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Início Int.</Label>
              <Input
                type="time"
                value={dia.intervalo_inicio || ''}
                onChange={(e) => handleHorarioChange('intervalo_inicio', e.target.value, idx, escalaState, setEscalaState)}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Fim Int.</Label>
              <Input
                type="time"
                value={dia.intervalo_fim || ''}
                onChange={(e) => handleHorarioChange('intervalo_fim', e.target.value, idx, escalaState, setEscalaState)}
                className="h-8 text-sm"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="escalas-ciclicas-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestão de Escalas</h1>
          <p className="text-slate-500">Gerencie escalas de trabalho e vínculos de empregados</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="escalas" data-testid="tab-escalas">
            <Calendar className="w-4 h-4 mr-2" />
            Escalas
          </TabsTrigger>
          <TabsTrigger value="vinculos" data-testid="tab-vinculos">
            <Users className="w-4 h-4 mr-2" />
            Vínculos
          </TabsTrigger>
        </TabsList>

        {/* ESCALAS TAB */}
        <TabsContent value="escalas" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={showEscalaDialog} onOpenChange={setShowEscalaDialog}>
              <DialogTrigger asChild>
                <Button data-testid="btn-nova-escala">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Escala
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Criar Escala</DialogTitle>
                  <DialogDescription>
                    Defina o nome e a quantidade de dias do ciclo. Configure cada dia como trabalho ou folga.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Nome da Escala</Label>
                    <Input
                      value={novaEscala.nome}
                      onChange={(e) => setNovaEscala({ ...novaEscala, nome: e.target.value })}
                      placeholder="Ex: Escala 12x36, Escala Semanal, Turno Rotativo"
                      data-testid="input-escala-nome"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Quantidade de Dias do Ciclo</Label>
                      <Input
                        type="number"
                        min="1"
                        max="60"
                        value={novaEscala.tamanho_ciclo}
                        onChange={(e) => updateDiasCiclo(parseInt(e.target.value) || 1)}
                        data-testid="input-escala-tamanho"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        O ciclo se repete a cada {novaEscala.tamanho_ciclo} dia(s)
                      </p>
                    </div>
                    <div>
                      <Label>Tolerância (min)</Label>
                      <Input
                        type="number"
                        value={novaEscala.tolerancia_minutos}
                        onChange={(e) => setNovaEscala({ ...novaEscala, tolerancia_minutos: parseInt(e.target.value) })}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Tolerância para atrasos
                      </p>
                    </div>
                  </div>

                  {novaEscala.dias.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <Label>Configuração dos Dias</Label>
                        <span className="text-sm text-slate-500">
                          {novaEscala.dias.filter(d => d.trabalha).length} trabalho / {novaEscala.dias.filter(d => !d.trabalha).length} folga
                        </span>
                      </div>
                      <div className="border rounded-lg p-4 max-h-72 overflow-y-auto space-y-2">
                        {novaEscala.dias.map((dia, idx) => renderDiaConfig(dia, idx, novaEscala, setNovaEscala))}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowEscalaDialog(false)}>Cancelar</Button>
                  <Button onClick={handleCreateEscala} data-testid="btn-salvar-escala">Criar Escala</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {escalas.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="text-center text-slate-500 py-8">
                  Nenhuma escala cadastrada. Clique em Nova Escala para criar.
                </CardContent>
              </Card>
            ) : (
              escalas.map((escala) => (
                <Card key={escala.id} data-testid={`escala-card-${escala.id}`} className="relative">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{escala.nome}</CardTitle>
                        <CardDescription>
                          Ciclo de {escala.tamanho_ciclo} dias
                        </CardDescription>
                      </div>
                      <Badge variant={escala.ativa !== false ? 'default' : 'secondary'}>
                        {escala.ativa !== false ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex flex-wrap gap-1">
                      {(escala.dias || []).map((dia, idx) => {
                        let bgClass = 'bg-slate-100 text-slate-500';
                        let title = 'Folga';
                        
                        // Suporte para nova estrutura (tipo_dia) e legada (trabalha/compensado)
                        const tipoDia = dia.tipo_dia || (dia.compensado ? 'compensado' : (dia.trabalha ? 'normal' : 'folga'));
                        
                        if (tipoDia === 'compensado') {
                          bgClass = 'bg-amber-100 text-amber-800';
                          title = 'Compensado';
                        } else if (tipoDia === 'normal') {
                          bgClass = 'bg-green-100 text-green-800';
                          title = 'Trabalha';
                        } else if (tipoDia === 'dsr') {
                          bgClass = 'bg-blue-100 text-blue-800';
                          title = 'DSR';
                        }
                        
                        return (
                          <div
                            key={idx}
                            className={`w-8 h-8 rounded flex items-center justify-center text-xs font-medium ${bgClass}`}
                            title={title}
                          >
                            {dia.dia_ciclo || idx + 1}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>Tolerância: {escala.tolerancia_minutos || 10} min</span>
                      <span>
                        {(escala.dias || []).filter(d => (d.tipo_dia === 'normal') || (d.trabalha && !d.compensado)).length}T / 
                        {(escala.dias || []).filter(d => d.tipo_dia === 'compensado' || d.compensado).length}C / 
                        {(escala.dias || []).filter(d => d.tipo_dia === 'folga' || d.tipo_dia === 'dsr' || (!d.trabalha && !d.compensado)).length}F
                      </span>
                    </div>
                    
                    {/* Botões de Ação */}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openEditDialog(escala)}
                        data-testid={`btn-editar-${escala.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openDeleteDialog(escala)}
                        data-testid={`btn-excluir-${escala.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* VINCULOS TAB */}
        <TabsContent value="vinculos" className="space-y-4">
          <div className="flex justify-end gap-2">
            {/* Botão Vincular em Lote */}
            <Dialog open={showVinculoLoteDialog} onOpenChange={(open) => {
              setShowVinculoLoteDialog(open);
              if (!open) {
                setEmpregadosSelecionados([]);
                setFiltroVinculo('todos');
              }
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="btn-vincular-lote">
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Vincular em Lote
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle>Vincular Empregados em Lote</DialogTitle>
                  <DialogDescription>
                    Selecione os empregados e aplique a mesma escala para todos.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                  {/* Filtros e seleção de escala */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Filtrar Empregados</Label>
                      <Select value={filtroVinculo} onValueChange={setFiltroVinculo}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos os ativos</SelectItem>
                          <SelectItem value="sem_escala">Sem escala vinculada</SelectItem>
                          <SelectItem value="com_escala">Com escala vinculada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Escala a Vincular</Label>
                      <Select
                        value={vinculoLote.escala_id}
                        onValueChange={(value) => setVinculoLote({ ...vinculoLote, escala_id: value })}
                      >
                        <SelectTrigger data-testid="select-lote-escala">
                          <SelectValue placeholder="Selecione a escala" />
                        </SelectTrigger>
                        <SelectContent>
                          {escalas.filter(e => e.ativa !== false).map((esc) => (
                            <SelectItem key={esc.id} value={esc.id}>
                              {esc.nome} ({esc.tamanho_ciclo} dias)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-600">Data de Início na Escala</Label>
                        <Input
                          type="date"
                          value={vinculoLote.data_inicio}
                          onChange={(e) => setVinculoLote({ ...vinculoLote, data_inicio: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Data da Primeira Folga <span className="text-red-500">*</span></Label>
                        <Input
                          type="date"
                          value={vinculoLote.data_primeira_folga}
                          onChange={(e) => setVinculoLote({ ...vinculoLote, data_primeira_folga: e.target.value })}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Atenção: Todos os empregados selecionados terão a mesma data de primeira folga.
                    </p>
                  </div>

                  {/* Lista de empregados com checkboxes */}
                  <div className="border rounded-lg overflow-hidden flex-1 flex flex-col">
                    <div className="bg-slate-50 px-4 py-2 border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={empregadosSelecionados.length === getEmpregadosFiltradosParaLote().length && getEmpregadosFiltradosParaLote().length > 0}
                          onCheckedChange={toggleTodosEmpregados}
                        />
                        <span className="text-sm font-medium">Selecionar Todos</span>
                      </div>
                      <Badge variant="secondary">
                        {empregadosSelecionados.length} selecionado(s)
                      </Badge>
                    </div>
                    <div className="overflow-y-auto max-h-[300px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12"></TableHead>
                            <TableHead>Nome</TableHead>
                            <TableHead>Cargo</TableHead>
                            <TableHead>Escala Atual</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {getEmpregadosFiltradosParaLote().map((emp) => {
                            const escalaAtual = escalas.find(e => e.id === emp.escala_id);
                            return (
                              <TableRow 
                                key={emp.id} 
                                className={empregadosSelecionados.includes(emp.id) ? 'bg-emerald-50' : ''}
                              >
                                <TableCell>
                                  <Checkbox
                                    checked={empregadosSelecionados.includes(emp.id)}
                                    onCheckedChange={() => toggleEmpregadoSelecionado(emp.id)}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">{emp.nome}</TableCell>
                                <TableCell className="text-sm text-slate-500">{emp.cargo || '-'}</TableCell>
                                <TableCell>
                                  {escalaAtual ? (
                                    <Badge variant="outline" className="text-xs">{escalaAtual.nome}</Badge>
                                  ) : (
                                    <span className="text-slate-400 text-xs">Sem escala</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {getEmpregadosFiltradosParaLote().length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-slate-400 py-8">
                                Nenhum empregado encontrado com o filtro selecionado
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>

                <DialogFooter className="border-t pt-4">
                  <Button variant="outline" onClick={() => setShowVinculoLoteDialog(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleVincularLote} 
                    disabled={vinculoLoteLoading || empregadosSelecionados.length === 0 || !vinculoLote.escala_id}
                    data-testid="btn-salvar-vinculo-lote"
                  >
                    {vinculoLoteLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Vinculando...
                      </>
                    ) : (
                      <>
                        <Users className="w-4 h-4 mr-2" />
                        Vincular {empregadosSelecionados.length} Empregado(s)
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Botão Vincular Individual */}
            <Dialog open={showVinculoDialog} onOpenChange={setShowVinculoDialog}>
              <DialogTrigger asChild>
                <Button data-testid="btn-novo-vinculo">
                  <Plus className="w-4 h-4 mr-2" />
                  Vincular Empregado
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Vincular Empregado à Escala</DialogTitle>
                  <DialogDescription>
                    Informe a data de início na escala e a data da primeira folga para cálculo correto do ciclo.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Empregado</Label>
                    <Select
                      value={novoVinculo.empregado_id}
                      onValueChange={(value) => setNovoVinculo({ ...novoVinculo, empregado_id: value })}
                    >
                      <SelectTrigger data-testid="select-vinculo-empregado">
                        <SelectValue placeholder="Selecione o empregado" />
                      </SelectTrigger>
                      <SelectContent>
                        {empregados
                          .filter(emp => emp.ativo !== false && !emp.nao_controla_ponto)
                          .map((emp) => {
                            const escalaAtual = escalas.find(e => e.id === emp.escala_id);
                            return (
                              <SelectItem key={emp.id} value={emp.id}>
                                <div className="flex items-center justify-between w-full">
                                  <span>{emp.nome}</span>
                                  {escalaAtual ? (
                                    <span className="ml-2 text-xs text-slate-500">
                                      ({escalaAtual.nome})
                                    </span>
                                  ) : (
                                    <span className="ml-2 text-xs text-amber-500">
                                      (sem escala)
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            );
                          })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Escala</Label>
                    <Select
                      value={novoVinculo.escala_id}
                      onValueChange={(value) => setNovoVinculo({ ...novoVinculo, escala_id: value })}
                    >
                      <SelectTrigger data-testid="select-vinculo-escala">
                        <SelectValue placeholder="Selecione a escala" />
                      </SelectTrigger>
                      <SelectContent>
                        {escalas.filter(e => e.ativa !== false).map((esc) => (
                          <SelectItem key={esc.id} value={esc.id}>
                            {esc.nome} ({esc.tamanho_ciclo} dias)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Data de Início na Escala</Label>
                      <Input
                        type="date"
                        value={novoVinculo.data_inicio}
                        onChange={(e) => setNovoVinculo({ ...novoVinculo, data_inicio: e.target.value })}
                        data-testid="input-vinculo-data-inicio"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Quando o empregado começou nesta escala
                      </p>
                    </div>
                    <div>
                      <Label>Data da Primeira Folga <span className="text-red-500">*</span></Label>
                      <Input
                        type="date"
                        value={novoVinculo.data_primeira_folga}
                        onChange={(e) => setNovoVinculo({ ...novoVinculo, data_primeira_folga: e.target.value })}
                        data-testid="input-vinculo-primeira-folga"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Primeira folga do empregado na escala
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label>Observação (opcional)</Label>
                    <Input
                      type="text"
                      value={novoVinculo.observacao}
                      onChange={(e) => setNovoVinculo({ ...novoVinculo, observacao: e.target.value })}
                      placeholder="Ex: Transferência do setor A"
                      data-testid="input-vinculo-obs"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowVinculoDialog(false)}>Cancelar</Button>
                  <Button onClick={handleVincularEmpregado} data-testid="btn-salvar-vinculo">Vincular</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Empregados e Suas Escalas</CardTitle>
              <CardDescription>Visualize e simule escalas futuras</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empregado</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead>Escala Atual</TableHead>
                    <TableHead>Ciclo / 1ª Folga</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {empregados
                    .filter(emp => emp.ativo !== false && !emp.nao_controla_ponto)
                    .map((emp) => {
                    const escalaVinculada = escalas.find(e => e.id === emp.escala_id);
                    return (
                      <TableRow key={emp.id}>
                        <TableCell className="font-medium">{emp.nome}</TableCell>
                        <TableCell>{emp.cargo || '-'}</TableCell>
                        <TableCell>
                          {escalaVinculada ? (
                            <Badge variant="outline">{escalaVinculada.nome}</Badge>
                          ) : (
                            <span className="text-amber-500 text-sm">Sem escala</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {escalaVinculada ? (
                            <div className="text-xs text-slate-500">
                              <span>{escalaVinculada.tamanho_ciclo} dias</span>
                              {emp.data_inicio_ciclo && (
                                <span className="ml-2">
                                  • 1ª folga: {emp.data_inicio_ciclo.split('-').reverse().join('/')}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setNovoVinculo({
                                ...novoVinculo,
                                empregado_id: emp.id,
                                escala_id: emp.escala_id || ''
                              });
                              setShowVinculoDialog(true);
                            }}
                            title="Vincular/Alterar escala"
                            data-testid={`btn-vincular-${emp.id}`}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          {escalaVinculada && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedEmpregado(emp);
                                handleSimular(escalaVinculada.id, emp.id);
                              }}
                              data-testid={`btn-simular-${emp.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Simular
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {empregados.filter(emp => emp.ativo !== false && !emp.nao_controla_ponto).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-400 py-8">
                        Nenhum empregado com controle de ponto encontrado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* DIALOG EDITAR ESCALA */}
      <Dialog open={showEditEscalaDialog} onOpenChange={setShowEditEscalaDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Escala</DialogTitle>
            <DialogDescription>Altere as configurações da escala.</DialogDescription>
          </DialogHeader>
          {selectedEscala && (
            <div className="space-y-4">
              <div>
                <Label>Nome da Escala</Label>
                <Input
                  value={selectedEscala.nome}
                  onChange={(e) => setSelectedEscala({ ...selectedEscala, nome: e.target.value })}
                  data-testid="input-edit-escala-nome"
                />
              </div>
              
              <div>
                <Label>Tolerância (min)</Label>
                <Input
                  type="number"
                  value={selectedEscala.tolerancia_minutos}
                  onChange={(e) => setSelectedEscala({ ...selectedEscala, tolerancia_minutos: parseInt(e.target.value) })}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label>Configuração dos Dias ({selectedEscala.tamanho_ciclo} dias)</Label>
                  <span className="text-sm text-slate-500">
                    {(selectedEscala.dias || []).filter(d => d.trabalha).length}T / {(selectedEscala.dias || []).filter(d => !d.trabalha).length}F
                  </span>
                </div>
                <div className="border rounded-lg p-4 max-h-72 overflow-y-auto space-y-2">
                  {(selectedEscala.dias || []).map((dia, idx) => renderDiaConfig(dia, idx, selectedEscala, setSelectedEscala))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditEscalaDialog(false)}>Cancelar</Button>
            <Button onClick={handleEditEscala} data-testid="btn-salvar-edicao">Salvar Alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG EDITAR TURNO */}
      <Dialog open={showEditTurnoDialog} onOpenChange={setShowEditTurnoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Turno</DialogTitle>
            <DialogDescription>Altere as configurações do turno.</DialogDescription>
          </DialogHeader>
          {selectedTurno && (
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input
                  value={selectedTurno.nome}
                  onChange={(e) => setSelectedTurno({ ...selectedTurno, nome: e.target.value })}
                  data-testid="input-edit-turno-nome"
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select
                  value={selectedTurno.tipo}
                  onValueChange={(value) => setSelectedTurno({ ...selectedTurno, tipo: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposTurno.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Hora Início</Label>
                  <Input
                    type="time"
                    value={selectedTurno.hora_inicio}
                    onChange={(e) => setSelectedTurno({ ...selectedTurno, hora_inicio: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Hora Fim</Label>
                  <Input
                    type="time"
                    value={selectedTurno.hora_fim}
                    onChange={(e) => setSelectedTurno({ ...selectedTurno, hora_fim: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Início Intervalo</Label>
                  <Input
                    type="time"
                    value={selectedTurno.intervalo_inicio || ''}
                    onChange={(e) => setSelectedTurno({ ...selectedTurno, intervalo_inicio: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Fim Intervalo</Label>
                  <Input
                    type="time"
                    value={selectedTurno.intervalo_fim || ''}
                    onChange={(e) => setSelectedTurno({ ...selectedTurno, intervalo_fim: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Horas Trabalhadas</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={selectedTurno.horas_trabalhadas || ''}
                  onChange={(e) => setSelectedTurno({ ...selectedTurno, horas_trabalhadas: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTurnoDialog(false)}>Cancelar</Button>
            <Button onClick={handleEditTurno} data-testid="btn-salvar-turno-edicao">Salvar Alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG EXCLUIR/INATIVAR ESCALA */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Excluir Escala
            </DialogTitle>
            <DialogDescription>
              {selectedEscala?.nome}
            </DialogDescription>
          </DialogHeader>
          
          {empregadosVinculados.length > 0 ? (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium">
                  Não é possível excluir esta escala.
                </p>
                <p className="text-red-600 text-sm mt-1">
                  Existem {empregadosVinculados.length} empregado(s) vinculado(s):
                </p>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                  {empregadosVinculados.slice(0, 5).map(emp => (
                    <li key={emp.id}>{emp.nome}</li>
                  ))}
                  {empregadosVinculados.length > 5 && (
                    <li>...e mais {empregadosVinculados.length - 5}</li>
                  )}
                </ul>
              </div>
              <p className="text-sm text-slate-600">
                Desvincule os empregados desta escala antes de excluí-la, ou inative a escala.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Fechar</Button>
                <Button variant="secondary" onClick={handleInativarEscala}>
                  <Power className="w-4 h-4 mr-1" />
                  Inativar Escala
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-slate-600">
                Esta escala não possui empregados vinculados. O que deseja fazer?
              </p>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
                <Button variant="secondary" onClick={handleInativarEscala}>
                  <Power className="w-4 h-4 mr-1" />
                  Inativar
                </Button>
                <Button variant="destructive" onClick={handleDeleteEscala}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Excluir Permanente
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SIMULADOR DIALOG */}
      <Dialog open={showSimuladorDialog} onOpenChange={setShowSimuladorDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Simulação de Escala - {simulacao?.escala?.nome || simulacao?.escala || 'N/A'}
            </DialogTitle>
            <DialogDescription>
              Previsão para os próximos 30 dias
            </DialogDescription>
          </DialogHeader>
          {simulacao && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="bg-slate-50 p-3 rounded">
                  <span className="text-slate-500">Tamanho do Ciclo</span>
                  <p className="font-semibold">{simulacao.tamanho_ciclo || simulacao.escala?.tamanho_ciclo || simulacao.escala?.dias?.length || '-'} dias</p>
                </div>
                <div className="bg-slate-50 p-3 rounded">
                  <span className="text-slate-500">Início do Ciclo</span>
                  <p className="font-semibold">{simulacao.data_inicio_ciclo}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded">
                  <span className="text-slate-500">Dias Simulados</span>
                  <p className="font-semibold">{simulacao.simulacao?.length || 0}</p>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Dia</TableHead>
                    <TableHead>Posição</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Turno</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {simulacao.simulacao?.map((dia, idx) => (
                    <TableRow key={idx} className={!dia.trabalha ? 'bg-slate-50' : ''}>
                      <TableCell className="font-mono">{dia.data}</TableCell>
                      <TableCell>{dia.dia_semana?.slice(0, 3)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{dia.posicao_ciclo}</Badge>
                      </TableCell>
                      <TableCell>
                        {dia.trabalha ? (
                          <Badge className="bg-green-500">Trabalha</Badge>
                        ) : (
                          <Badge variant="secondary">Folga</Badge>
                        )}
                      </TableCell>
                      <TableCell>{dia.turno?.nome || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
